"use client";

import { c as _c } from "react/compiler-runtime";
import { ClipboardList } from "lucide-react";
import { usePathname, useRouter } from "next/navigation";
import { useState } from "react";
import { CardActions, createCardActionsProps } from "@/src/components/cards/card-actions";
import { EntityDropdownMenu, LinkToEntity } from "@/src/components/cards/common";
import { DiagnosisListTable } from "@/src/components/cards/diagnosis-list-table";
import ReferralCardEditable from "@/src/components/cards/referral-card/referral-card-editable";
import { ReferralTagBadges } from "@/src/components/referrals/referral-tag-badges";
import { Badge } from "@/src/components/shadcn/ui/badge";
import { ContextMenu, ContextMenuTrigger } from "@/src/components/shadcn/ui/context-menu";
import { referralStatusIcons, defaultReferralStatusIconAndColor, schedulingPriorityIcons } from "@/src/components/tasks/model-icons";
import { ValerieCard, ValerieCardField, ValerieCardSection, ValerieCardSectionTitle, ValerieCardSections } from "@/src/components/valerie-card";
import { useUser } from "@/src/context/user-context";
import { usePriorAuthorizationsByReferralId } from "@/src/lib/data-fetchers/prior-authorization-fetchers";
import { canWriteSOR } from "@/src/lib/rbac";
import { formatFaxParens, formatIsoDate } from "@/src/lib/utils/date-and-phone-utils";
import { isStatOrUrgentPriority, Referral, ReferralStatus, SchedulingPriority, referralStatusLabel } from "@/src/models/documents/referral";
import { getPatientName } from "@/src/models/patient";
export default function ReferralCard(t0) {
  const $ = _c(135);
  const {
    referral,
    isEditable: t1
  } = t0;
  const isEditable = t1 === undefined ? false : t1;
  const [isEditing, setIsEditing] = useState(false);
  const pathname = usePathname();
  const router = useRouter();
  const {
    roles
  } = useUser();
  let t2;
  if ($[0] !== roles) {
    t2 = canWriteSOR(roles);
    $[0] = roles;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const canWriteSor = t2;
  const effectiveIsEditable = isEditable;
  const {
    priorAuthorizations
  } = usePriorAuthorizationsByReferralId(referral?.id ?? null);
  let T0;
  let T1;
  let T2;
  let T3;
  let cardActionsProps;
  let diagnosisList;
  let hasDiagnoses;
  let referringFax;
  let t10;
  let t11;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[2] !== canWriteSor || $[3] !== effectiveIsEditable || $[4] !== isEditing || $[5] !== pathname || $[6] !== priorAuthorizations || $[7] !== referral || $[8] !== router) {
    t11 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const authExpirationDate = priorAuthorizations.find(_temp)?.endDate ?? null;
      if (!referral) {
        t11 = null;
        break bb0;
      }
      let t12;
      if ($[26] !== isEditing) {
        t12 = () => {
          setIsEditing(!isEditing);
        };
        $[26] = isEditing;
        $[27] = t12;
      } else {
        t12 = $[27];
      }
      const handleEditToggle = t12;
      if (isEditing) {
        let t13;
        if ($[28] !== handleEditToggle || $[29] !== referral) {
          t13 = <ReferralCardEditable referral={referral} onClose={handleEditToggle} />;
          $[28] = handleEditToggle;
          $[29] = referral;
          $[30] = t13;
        } else {
          t13 = $[30];
        }
        t11 = t13;
        break bb0;
      }
      const patientDisplayId = referral.patient?.displayId;
      let t13;
      if ($[31] !== referral.patient) {
        t13 = referral.patient ? getPatientName(referral.patient) : null;
        $[31] = referral.patient;
        $[32] = t13;
      } else {
        t13 = $[32];
      }
      const patientName = t13;
      let t14;
      if ($[33] !== canWriteSor || $[34] !== handleEditToggle || $[35] !== patientDisplayId || $[36] !== referral.displayId || $[37] !== referral.id || $[38] !== router) {
        const customActions = [...(canWriteSor ? [{
          label: "Edit Referral",
          onClick: handleEditToggle
        }] : []), ...(patientDisplayId ? [{
          label: "View Patient",
          onClick: () => router.push(`/patients/${patientDisplayId}`)
        }] : [])];
        t14 = createCardActionsProps({
          id: referral.id,
          label: "Referral ID",
          href: `/referrals/${referral.displayId}`,
          customProps: customActions
        });
        $[33] = canWriteSor;
        $[34] = handleEditToggle;
        $[35] = patientDisplayId;
        $[36] = referral.displayId;
        $[37] = referral.id;
        $[38] = router;
        $[39] = t14;
      } else {
        t14 = $[39];
      }
      cardActionsProps = t14;
      let t15;
      if ($[40] !== referral.diagnosisList) {
        t15 = Array.isArray(referral.diagnosisList) ? referral.diagnosisList : [];
        $[40] = referral.diagnosisList;
        $[41] = t15;
      } else {
        t15 = $[41];
      }
      diagnosisList = t15;
      hasDiagnoses = diagnosisList.length > 0 || Boolean(referral.diagnosisCode || referral.diagnosisText);
      const status = referral.status as ReferralStatus | null;
      const statusMeta = referralStatusIcons[status || ReferralStatus.NOT_STARTED] || defaultReferralStatusIconAndColor;
      const StatusIcon = statusMeta?.icon;
      const schedulingPriority = referral.schedulingPriority as SchedulingPriority | null;
      const schedulingPriorityMeta = schedulingPriority ? schedulingPriorityIcons[schedulingPriority] : null;
      const SchedulingPriorityIcon = schedulingPriorityMeta?.icon;
      let t16;
      if ($[42] !== schedulingPriority) {
        t16 = isStatOrUrgentPriority(schedulingPriority);
        $[42] = schedulingPriority;
        $[43] = t16;
      } else {
        t16 = $[43];
      }
      const showSchedulingPriorityFlag = t16;
      const referringFaxNumber = referral.referringLocation?.faxPhoneNumber;
      let t17;
      if ($[44] !== referringFaxNumber) {
        t17 = referringFaxNumber ? formatFaxParens(referringFaxNumber) : null;
        $[44] = referringFaxNumber;
        $[45] = t17;
      } else {
        t17 = $[45];
      }
      referringFax = t17;
      T3 = ContextMenu;
      T2 = ContextMenuTrigger;
      t10 = true;
      T1 = ValerieCard;
      t5 = "w-full";
      if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
        t6 = <ClipboardList />;
        $[46] = t6;
      } else {
        t6 = $[46];
      }
      let t18;
      if ($[47] !== SchedulingPriorityIcon || $[48] !== schedulingPriorityMeta?.color || $[49] !== showSchedulingPriorityFlag) {
        t18 = showSchedulingPriorityFlag && SchedulingPriorityIcon && <SchedulingPriorityIcon weight="fill" className={`h-4 w-4 ${schedulingPriorityMeta?.color ?? ""}`} />;
        $[47] = SchedulingPriorityIcon;
        $[48] = schedulingPriorityMeta?.color;
        $[49] = showSchedulingPriorityFlag;
        $[50] = t18;
      } else {
        t18 = $[50];
      }
      let t19;
      if ($[51] !== referral.section?.reviewedContent?.isVaMilitaryReferral) {
        t19 = !!referral.section?.reviewedContent?.isVaMilitaryReferral && <Badge className="bg-yellow-500 text-white dark:bg-yellow-600">VA / Military / ADSM</Badge>;
        $[51] = referral.section?.reviewedContent?.isVaMilitaryReferral;
        $[52] = t19;
      } else {
        t19 = $[52];
      }
      let t20;
      if ($[53] !== referral.tags) {
        t20 = <ReferralTagBadges tags={referral.tags} />;
        $[53] = referral.tags;
        $[54] = t20;
      } else {
        t20 = $[54];
      }
      if ($[55] !== t18 || $[56] !== t19 || $[57] !== t20) {
        t7 = <span className="inline-flex items-center gap-2">Referral{t18}{t19}{t20}</span>;
        $[55] = t18;
        $[56] = t19;
        $[57] = t20;
        $[58] = t7;
      } else {
        t7 = $[58];
      }
      let t21;
      if ($[59] !== patientName) {
        t21 = {
          label: "Patient",
          value: patientName
        };
        $[59] = patientName;
        $[60] = t21;
      } else {
        t21 = $[60];
      }
      let t22;
      if ($[61] !== referral.dateOfReferral) {
        t22 = referral.dateOfReferral ? formatIsoDate(referral.dateOfReferral) : null;
        $[61] = referral.dateOfReferral;
        $[62] = t22;
      } else {
        t22 = $[62];
      }
      let t23;
      if ($[63] !== t22) {
        t23 = {
          label: "Referred",
          value: t22
        };
        $[63] = t22;
        $[64] = t23;
      } else {
        t23 = $[64];
      }
      let t24;
      if ($[65] !== referral.dateReceived) {
        t24 = referral.dateReceived ? formatIsoDate(referral.dateReceived) : null;
        $[65] = referral.dateReceived;
        $[66] = t24;
      } else {
        t24 = $[66];
      }
      let t25;
      if ($[67] !== t24) {
        t25 = {
          label: "Received",
          value: t24
        };
        $[67] = t24;
        $[68] = t25;
      } else {
        t25 = $[68];
      }
      if ($[69] !== t21 || $[70] !== t23 || $[71] !== t25) {
        t8 = [t21, t23, t25];
        $[69] = t21;
        $[70] = t23;
        $[71] = t25;
        $[72] = t8;
      } else {
        t8 = $[72];
      }
      let t26;
      if ($[73] !== pathname || $[74] !== referral.displayId) {
        t26 = !pathname?.startsWith(`/referrals/${referral.displayId}`) ? <LinkToEntity href={`/referrals/${referral.displayId}`} /> : null;
        $[73] = pathname;
        $[74] = referral.displayId;
        $[75] = t26;
      } else {
        t26 = $[75];
      }
      let t27;
      if ($[76] !== cardActionsProps || $[77] !== effectiveIsEditable) {
        t27 = effectiveIsEditable && <EntityDropdownMenu cardActionsProps={cardActionsProps} />;
        $[76] = cardActionsProps;
        $[77] = effectiveIsEditable;
        $[78] = t27;
      } else {
        t27 = $[78];
      }
      if ($[79] !== t26 || $[80] !== t27) {
        t9 = <>{t26}{t27}</>;
        $[79] = t26;
        $[80] = t27;
        $[81] = t9;
      } else {
        t9 = $[81];
      }
      T0 = ValerieCardSections;
      let t28;
      if ($[82] !== StatusIcon || $[83] !== statusMeta?.color) {
        t28 = StatusIcon && <StatusIcon weight="fill" className={`h-4 w-4 ${statusMeta?.color ?? ""}`} />;
        $[82] = StatusIcon;
        $[83] = statusMeta?.color;
        $[84] = t28;
      } else {
        t28 = $[84];
      }
      const t29 = status || ReferralStatus.NOT_STARTED;
      let t30;
      if ($[85] !== t29) {
        t30 = referralStatusLabel(t29);
        $[85] = t29;
        $[86] = t30;
      } else {
        t30 = $[86];
      }
      let t31;
      if ($[87] !== t30) {
        t31 = <span className="no-select">{t30}</span>;
        $[87] = t30;
        $[88] = t31;
      } else {
        t31 = $[88];
      }
      let t32;
      if ($[89] !== t28 || $[90] !== t31) {
        t32 = <ValerieCardField label="Status"><div className="flex items-center gap-2">{t28}{t31}</div></ValerieCardField>;
        $[89] = t28;
        $[90] = t31;
        $[91] = t32;
      } else {
        t32 = $[91];
      }
      let t33;
      if ($[92] !== referral.serviceLine) {
        t33 = <ValerieCardField label="Service Line" value={referral.serviceLine} />;
        $[92] = referral.serviceLine;
        $[93] = t33;
      } else {
        t33 = $[93];
      }
      let t34;
      if ($[94] !== referral.schedulingPriority) {
        t34 = <ValerieCardField label="Scheduling Priority" value={referral.schedulingPriority} />;
        $[94] = referral.schedulingPriority;
        $[95] = t34;
      } else {
        t34 = $[95];
      }
      let t35;
      if ($[96] !== referral.reasonForReferral) {
        t35 = <ValerieCardField label="Reason for Referral" value={referral.reasonForReferral} colSpan={3} />;
        $[96] = referral.reasonForReferral;
        $[97] = t35;
      } else {
        t35 = $[97];
      }
      if ($[98] !== t32 || $[99] !== t33 || $[100] !== t34 || $[101] !== t35) {
        t3 = <ValerieCardSection title="Referral">{t32}{t33}{t34}{t35}</ValerieCardSection>;
        $[98] = t32;
        $[99] = t33;
        $[100] = t34;
        $[101] = t35;
        $[102] = t3;
      } else {
        t3 = $[102];
      }
      t4 = (referral.authorizationNumber || authExpirationDate) && <ValerieCardSection title="Authorization"><ValerieCardField label="Authorization Number" value={referral.authorizationNumber} /><ValerieCardField label="Expiration Date" value={authExpirationDate ? formatIsoDate(authExpirationDate) : null} /></ValerieCardSection>;
    }
    $[2] = canWriteSor;
    $[3] = effectiveIsEditable;
    $[4] = isEditing;
    $[5] = pathname;
    $[6] = priorAuthorizations;
    $[7] = referral;
    $[8] = router;
    $[9] = T0;
    $[10] = T1;
    $[11] = T2;
    $[12] = T3;
    $[13] = cardActionsProps;
    $[14] = diagnosisList;
    $[15] = hasDiagnoses;
    $[16] = referringFax;
    $[17] = t10;
    $[18] = t11;
    $[19] = t3;
    $[20] = t4;
    $[21] = t5;
    $[22] = t6;
    $[23] = t7;
    $[24] = t8;
    $[25] = t9;
  } else {
    T0 = $[9];
    T1 = $[10];
    T2 = $[11];
    T3 = $[12];
    cardActionsProps = $[13];
    diagnosisList = $[14];
    hasDiagnoses = $[15];
    referringFax = $[16];
    t10 = $[17];
    t11 = $[18];
    t3 = $[19];
    t4 = $[20];
    t5 = $[21];
    t6 = $[22];
    t7 = $[23];
    t8 = $[24];
    t9 = $[25];
  }
  if (t11 !== Symbol.for("react.early_return_sentinel")) {
    return t11;
  }
  let t12;
  if ($[103] !== referringFax) {
    t12 = referringFax && <ValerieCardSection title="Referring Location"><ValerieCardField label="Fax" value={referringFax} /></ValerieCardSection>;
    $[103] = referringFax;
    $[104] = t12;
  } else {
    t12 = $[104];
  }
  let t13;
  if ($[105] !== diagnosisList || $[106] !== hasDiagnoses || $[107] !== referral.diagnosisCode || $[108] !== referral.diagnosisText) {
    t13 = hasDiagnoses && <section className="space-y-2"><ValerieCardSectionTitle>ICD-10 Diagnosis List</ValerieCardSectionTitle><DiagnosisListTable diagnoses={diagnosisList} fallbackDiagnosis={referral.diagnosisCode || referral.diagnosisText ? {
        diagnosisCode: referral.diagnosisCode ?? null,
        diagnosisText: referral.diagnosisText ?? null
      } : null} /></section>;
    $[105] = diagnosisList;
    $[106] = hasDiagnoses;
    $[107] = referral.diagnosisCode;
    $[108] = referral.diagnosisText;
    $[109] = t13;
  } else {
    t13 = $[109];
  }
  let t14;
  if ($[110] !== T0 || $[111] !== t12 || $[112] !== t13 || $[113] !== t3 || $[114] !== t4) {
    t14 = <T0>{t3}{t4}{t12}{t13}</T0>;
    $[110] = T0;
    $[111] = t12;
    $[112] = t13;
    $[113] = t3;
    $[114] = t4;
    $[115] = t14;
  } else {
    t14 = $[115];
  }
  let t15;
  if ($[116] !== T1 || $[117] !== t14 || $[118] !== t5 || $[119] !== t6 || $[120] !== t7 || $[121] !== t8 || $[122] !== t9) {
    t15 = <T1 className={t5} icon={t6} title={t7} details={t8} actions={t9}>{t14}</T1>;
    $[116] = T1;
    $[117] = t14;
    $[118] = t5;
    $[119] = t6;
    $[120] = t7;
    $[121] = t8;
    $[122] = t9;
    $[123] = t15;
  } else {
    t15 = $[123];
  }
  let t16;
  if ($[124] !== T2 || $[125] !== t10 || $[126] !== t15) {
    t16 = <T2 asChild={t10}>{t15}</T2>;
    $[124] = T2;
    $[125] = t10;
    $[126] = t15;
    $[127] = t16;
  } else {
    t16 = $[127];
  }
  let t17;
  if ($[128] !== cardActionsProps || $[129] !== effectiveIsEditable) {
    t17 = effectiveIsEditable && <CardActions {...cardActionsProps} isContextMenu={true} />;
    $[128] = cardActionsProps;
    $[129] = effectiveIsEditable;
    $[130] = t17;
  } else {
    t17 = $[130];
  }
  let t18;
  if ($[131] !== T3 || $[132] !== t16 || $[133] !== t17) {
    t18 = <T3>{t16}{t17}</T3>;
    $[131] = T3;
    $[132] = t16;
    $[133] = t17;
    $[134] = t18;
  } else {
    t18 = $[134];
  }
  return t18;
}
function _temp(pa) {
  return pa.endDate;
}